import React from 'react';
import { COLORS } from '../../../utils/colors';
import { Box } from '@material-ui/core';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more.js';
import xRange from 'highcharts/modules/xrange.js';
import { useTranslation } from 'react-i18next';
import { Alert } from '@mui/material';
import { GraphInfo } from './GraphInfo';

highchartsMore(Highcharts);
xRange(Highcharts);

export function AvailabilityConsolidatedByStep({
  dataConsolidated,
  dataAvailabilityConsolidatedByStep,
}) {
  const chartConsolidatedRef = React.useRef(null);
  const chartNodeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const { t } = useTranslation();
  const [hoverData, setHoverData] = React.useState(null);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (chartConsolidatedRef.current) {
        chartConsolidatedRef.current.chart.reflow();
      }
      if (chartNodeRef.current) {
        chartNodeRef.current.chart.reflow();
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, []);

  function onHoverData(e) {
    const { x, x2, y, series } = e;
    let point = [];
    point['x'] = x;
    point['x2'] = x2;
    point['y'] = series.chart.yAxis[0].categories[y];
    let step = series.userOptions.name;
    setHoverData({ ...point, step });
  }

  function processData() {
    if (
      !dataAvailabilityConsolidatedByStep ||
      Object.keys(dataAvailabilityConsolidatedByStep).length === 0 ||
      !dataConsolidated ||
      Object.keys(dataConsolidated).length === 0
    )
      return {};

    const { Paso } = dataConsolidated.Objetivo;
    const { estado } = dataAvailabilityConsolidatedByStep.Objetivo.Nodo[0];

    let dictDataStackedBarUptime = {};
    dictDataStackedBarUptime['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.uptime'
    );
    dictDataStackedBarUptime['pointWidth'] = 40;
    let arrayDataUptime = [];

    let dictDataStackedBarDowntime = {};
    dictDataStackedBarDowntime['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.downtime'
    );
    dictDataStackedBarDowntime['pointWidth'] = 40;
    let arrayDataDowntime = [];

    let dictDataStackedBarNoMon = {};
    dictDataStackedBarNoMon['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.no-monitoring'
    );
    dictDataStackedBarNoMon['pointWidth'] = 40;
    let arrayDataNoMon = [];

    let dictDataStackedBarPartial = {};
    dictDataStackedBarPartial['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.partial'
    );
    dictDataStackedBarPartial['pointWidth'] = 40;
    let arrayDataPartial = [];

    let dictDataStackedBarEvent = {};
    dictDataStackedBarEvent['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.maintenance'
    );
    dictDataStackedBarEvent['pointWidth'] = 40;
    let arrayDataEvent = [];

    let dictDataStackedBarMarcadoAtentus = {};
    dictDataStackedBarMarcadoAtentus['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.atentus-marked-period'
    );
    dictDataStackedBarMarcadoAtentus['pointWidth'] = 40;
    let arrayDataMarcadoAtentus = [];

    //Diccionarios y listas para información por nodos
    let dictDataStackedBarUptimeNode = {};
    dictDataStackedBarUptimeNode['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.uptime'
    );
    dictDataStackedBarUptimeNode['pointWidth'] = 20;
    let arrayDataUptimeNode = [];

    let dictDataStackedBarDowntimeNode = {};
    dictDataStackedBarDowntimeNode['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.downtime'
    );
    dictDataStackedBarDowntimeNode['pointWidth'] = 20;
    let arrayDataDowntimeNode = [];

    let dictDataStackedBarNoMonNode = {};
    dictDataStackedBarNoMonNode['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.no-monitoring'
    );
    dictDataStackedBarNoMonNode['pointWidth'] = 20;
    let arrayDataNoMonNode = [];

    let dictDataStackedBarPartialNode = {};
    dictDataStackedBarPartialNode['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.partial'
    );
    dictDataStackedBarPartialNode['pointWidth'] = 20;
    let arrayDataPartialNode = [];

    let dictDataStackedBarEventNode = {};
    dictDataStackedBarEventNode['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.maintenance'
    );
    dictDataStackedBarEventNode['pointWidth'] = 20;
    let arrayDataEventNode = [];

    let dictDataStackedBarMarcadoAtentusNode = {};
    dictDataStackedBarMarcadoAtentusNode['name'] = t(
      'availability-consolidated-per-step.chart.bar.name.atentus-marked-period'
    );
    dictDataStackedBarMarcadoAtentusNode['pointWidth'] = 20;
    let arrayDataMarcadoAtentusNode = [];

    Object.keys(estado).map((key) => {
      let dictDataStackedBar = {};

      let dateJson = new Date(estado[key]['fecha_desde']);
      let year = dateJson.getFullYear();
      let month = dateJson.getMonth();
      let day = dateJson.getDate();
      let hours = dateJson.getHours();
      let minutes = dateJson.getMinutes();
      let seconds = dateJson.getSeconds();

      let dateJsonEnd = new Date(estado[key]['fecha_hasta']);
      let yearEnd = dateJsonEnd.getFullYear();
      let monthEnd = dateJsonEnd.getMonth();
      let dayEnd = dateJsonEnd.getDate();
      let hoursEnd = dateJsonEnd.getHours();
      let minutesEnd = dateJsonEnd.getMinutes();
      let secondsEnd = dateJsonEnd.getSeconds();

      dictDataStackedBar['x'] = Date.UTC(
        year,
        month,
        day,
        hours,
        minutes,
        seconds
      );
      dictDataStackedBar['x2'] = Date.UTC(
        yearEnd,
        monthEnd,
        dayEnd,
        hoursEnd,
        minutesEnd,
        secondsEnd
      );
      dictDataStackedBar['y'] = 0;
      if (estado[key]['nombre'] == 'uptime') {
        dictDataStackedBar['color'] = COLORS.UPTIME;
        arrayDataUptime.push(dictDataStackedBar);
      } else if (estado[key]['nombre'] == 'downtime') {
        dictDataStackedBar['color'] = COLORS.DOWNTIME;
        arrayDataDowntime.push(dictDataStackedBar);
      } else if (estado[key]['nombre'] == 'no monitoreo') {
        dictDataStackedBar['color'] = COLORS.NO_MONITOREO;
        arrayDataNoMon.push(dictDataStackedBar);
      } else if (estado[key]['nombre'] == 'downtime parcial') {
        dictDataStackedBar['color'] = COLORS.DOWNTIME_PARCIAL;
        arrayDataPartial.push(dictDataStackedBar);
      } else if (estado[key]['nombre'] == 'evento cliente') {
        dictDataStackedBar['color'] = COLORS.EVENTO_CLIENTE;
        arrayDataEvent.push(dictDataStackedBar);
      } else if (estado[key]['nombre'] == 'marcado atentus') {
        dictDataStackedBar['color'] = COLORS.MARCADO_ATENTUS;
        arrayDataMarcadoAtentus.push(dictDataStackedBar);
      }
    });

    let categoriedStep = [];
    Object.keys(Paso).map((keyPaso) => {
      let estadoPaso = Paso[keyPaso]['estado'][0]['estadoDetalle'];

      categoriedStep.push(Paso[keyPaso]['paso_nombre']);

      Object.keys(estadoPaso).map((key) => {
        let dictDataStackedBar = {};

        let dateJson = new Date(
          estadoPaso[key]['fecha_desde'].substring(0, 19)
        );
        let year = dateJson.getFullYear();
        let month = dateJson.getMonth();
        let day = dateJson.getDate();
        let hours = dateJson.getHours();
        let minutes = dateJson.getMinutes();
        let seconds = dateJson.getSeconds();

        let dateJsonEnd = new Date(
          estadoPaso[key]['fecha_hasta'].substring(0, 19)
        );
        let yearEnd = dateJsonEnd.getFullYear();
        let monthEnd = dateJsonEnd.getMonth();
        let dayEnd = dateJsonEnd.getDate();
        let hoursEnd = dateJsonEnd.getHours();
        let minutesEnd = dateJsonEnd.getMinutes();
        let secondsEnd = dateJsonEnd.getSeconds();

        dictDataStackedBar['x'] = Date.UTC(
          year,
          month,
          day,
          hours,
          minutes,
          seconds
        );
        dictDataStackedBar['x2'] = Date.UTC(
          yearEnd,
          monthEnd,
          dayEnd,
          hoursEnd,
          minutesEnd,
          secondsEnd
        );
        dictDataStackedBar['y'] = parseInt(keyPaso);
        if (estadoPaso[key]['nombre'] == 'uptime') {
          dictDataStackedBar['color'] = COLORS.UPTIME;
          arrayDataUptimeNode.push(dictDataStackedBar);
        } else if (estadoPaso[key]['nombre'] == 'downtime') {
          dictDataStackedBar['color'] = COLORS.DOWNTIME;
          arrayDataDowntimeNode.push(dictDataStackedBar);
        } else if (estadoPaso[key]['nombre'] == 'no monitoreo') {
          dictDataStackedBar['color'] = COLORS.NO_MONITOREO;
          arrayDataNoMonNode.push(dictDataStackedBar);
        } else if (estadoPaso[key]['nombre'] == 'downtime parcial') {
          dictDataStackedBar['color'] = COLORS.DOWNTIME_PARCIAL;
          arrayDataPartialNode.push(dictDataStackedBar);
        } else if (estadoPaso[key]['nombre'] == 'evento cliente') {
          dictDataStackedBar['color'] = COLORS.EVENTO_CLIENTE;
          arrayDataEventNode.push(dictDataStackedBar);
        } else if (estadoPaso[key]['nombre'] == 'marcado atentus') {
          dictDataStackedBar['color'] = COLORS.MARCADO_ATENTUS;
          arrayDataMarcadoAtentusNode.push(dictDataStackedBar);
        }
      });
    });
    dictDataStackedBarUptime['data'] = arrayDataUptime;
    dictDataStackedBarDowntime['data'] = arrayDataDowntime;
    dictDataStackedBarNoMon['data'] = arrayDataNoMon;
    dictDataStackedBarPartial['data'] = arrayDataPartial;
    dictDataStackedBarEvent['data'] = arrayDataEvent;
    dictDataStackedBarMarcadoAtentus['data'] = arrayDataMarcadoAtentus;

    dictDataStackedBarUptimeNode['data'] = arrayDataUptimeNode;
    dictDataStackedBarDowntimeNode['data'] = arrayDataDowntimeNode;
    dictDataStackedBarNoMonNode['data'] = arrayDataNoMonNode;
    dictDataStackedBarPartialNode['data'] = arrayDataPartialNode;
    dictDataStackedBarEventNode['data'] = arrayDataEventNode;
    dictDataStackedBarMarcadoAtentusNode['data'] = arrayDataMarcadoAtentusNode;

    let heightNodeChart = 400;
    if (Paso.length < 3) {
      heightNodeChart = 150;
    } else if (Paso.length > 5 && Paso.length < 10) {
      heightNodeChart = 400;
    } else if (Paso.length > 10) {
      heightNodeChart = 900;
    }

    return {
      chartOptionsStackedBarConsolidated: {
        chart: {
          type: 'xrange',
          height: 80,
        },
        title: {
          text: '',
          style: {
            fontSize: '',
          },
        },
        credits: {
          enabled: false,
        },
        yAxis: {
          categories: ['Consolidado'],
          title: {
            text: '',
          },
          labels: {
            useHTML: true,
            style: {
              whiteSpace: 'normal',
            },
            step: 1,
            formatter: function () {
              //use formatter
              return (
                '<div align="left" style="word-wrap: break-word;word-break: break-all;width:150px">' +
                this.value +
                '</div>'
              );
            },
          },
        },
        series: [
          dictDataStackedBarUptime,
          dictDataStackedBarDowntime,
          dictDataStackedBarNoMon,
          dictDataStackedBarPartial,
          dictDataStackedBarEvent,
          dictDataStackedBarMarcadoAtentus,
        ],
        plotOptions: {
          series: {
            borderRadius: '0px',
            borderColor: 'transparent',
            turboThreshold: 30000,
            boostThreshold: 1,
            datalabels: false,
            cursor: 'pointer',
            point: {
              events: {
                mouseOver: function (event) {
                  onHoverData(this);
                },
              },
            },
          },
          xrange: {
            dataLabels: {
              enabled: false,
            },
            borderRadius: 0,
            grouping: false,
            borderWidth: 0,
            pointPadding: 10,
            borderColor: 'none',
          },
        },
        legend: {
          enabled: false,
        },
        xAxis: {
          type: 'datetime',
          gridLineWidth: 0,
          dateTimeLabelFormats: {
            second: '%l:%M:%S%P',
            minute: '%l:%M%P',
            hour: '%H:%M',
            day: '<br/><b>%e/%b</b>',
            week: '%e. %b',
            month: '%b %y',
            year: '%Y',
          },
          labels: {
            enabled: false,
          },
          visible: false,
        },
      },
      chartOptionsStackedBarNode: {
        chart: {
          type: 'xrange',
          height: heightNodeChart,
        },
        title: null,
        subtitle: null,
        credits: {
          enabled: false,
        },
        xAxis: {
          type: 'datetime',
          gridLineWidth: 0,
          dateTimeLabelFormats: {
            second: '%l:%M:%S%P',
            minute: '%l:%M%P',
            hour: '%H:%M',
            day: '<br/><b>%e/%b</b>',
            week: '%e. %b',
            month: '%b %y',
            year: '%Y',
          },
        },
        yAxis: {
          categories: categoriedStep,
          title: {
            text: '',
          },
          gridLineWidth: 0,
          reversed: true,
          labels: {
            useHTML: true,
            style: {
              whiteSpace: 'normal',
            },
            step: 1,
            formatter: function () {
              //use formatter
              return (
                '<div align="left" style="word-wrap: break-word;word-break: break-all;width:150px">' +
                this.value +
                '</div>'
              );
            },
          },
        },
        plotOptions: {
          series: {
            cursor: 'pointer',
            turboThreshold: 30000,
            boostThreshold: 1,
            point: {
              events: {
                mouseOver: function (event) {
                  onHoverData(this);
                },
              },
            },
          },
          xrange: {
            dataLabels: {
              enabled: false,
            },
            borderRadius: 0,
            grouping: false,
            borderWidth: 0,
            pointPadding: 0,
            borderColor: 'none',
          },
        },
        series: [
          dictDataStackedBarUptimeNode,
          dictDataStackedBarDowntimeNode,
          dictDataStackedBarNoMonNode,
          dictDataStackedBarPartialNode,
          dictDataStackedBarEventNode,
          dictDataStackedBarMarcadoAtentusNode,
        ],
        legend: { enabled: false },
      },
    };
  }

  if (
    !dataAvailabilityConsolidatedByStep ||
    Object.keys(dataAvailabilityConsolidatedByStep).length === 0 ||
    !dataConsolidated ||
    Object.keys(dataConsolidated).length === 0
  ) {
    return <Alert severity="info">{t('objective-not-data')}</Alert>;
  }

  const {
    chartOptionsStackedBarConsolidated,
    chartOptionsStackedBarNode,
  } = processData();

  return (
    <Box
      ref={containerRef}
      style={{
        height: '100%',
        minWidth: '100%',
      }}
    >
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptionsStackedBarConsolidated}
        ref={chartConsolidatedRef}
        containerProps={{ style: { width: '100%' } }}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptionsStackedBarNode}
        ref={chartNodeRef}
        containerProps={{ style: { width: '100%' } }}
      />
      <GraphInfo {...hoverData} />
    </Box>
  );
}
